<template>
  <b-modal
    v-model="editorShown"
    title="New Laboratory Order"
    @hidden="onHidden"
    @ok="submitLaboratoryOrder"
    @cancel="close"
    @close="close"
    centered
    size="md-lg"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok()">
        Save
      </b-button>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Patient"
          :state="formErrors.episodeOfCare.length == 0 ? null : false"
          :invalid-feedback="formErrors.episodeOfCare.join(', ')"
        >
          <eoc-picker
            v-model="form.episodeOfCare"
            class=""
            :eocs="activeClients"
            :async="true"
            @search="
              ({ search, loading }) => fetchEocsOptionsPicker(search, loading)
            "
        /></b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Collection date & time"
          :state="formErrors.collectionDateTime.length == 0 ? null : false"
          :invalid-feedback="formErrors.collectionDateTime.join(', ')"
        >
          <date-time-picker v-model="form.collectionDateTime" />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label="Laboratory"
          :state="formErrors.laboratory.length == 0 ? null : false"
          :invalid-feedback="formErrors.laboratory.join(', ')"
        >
          <v-select
            v-model="form.laboratory"
            :options="laboratories"
            @input="fetchPanelsAndTests()"
          ></v-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group v-if="panels && form.laboratory.value" label="Panel">
          <v-select v-model="form.laboratoryPanel" :options="panels"></v-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-form-group
      v-if="tests && form.laboratory.value"
      label="Tests"
      :state="formErrors.testIds.length == 0 ? null : false"
      :invalid-feedback="formErrors.testIds.join(', ')"
    >
      <b-row style="max-height:200px; overflow:auto">
        <b-col v-for="(chunk, chunkIndex) in chunks(tests, 2)" :key="chunkIndex"
          ><b-form-group>
            <b-form-checkbox-group id="checkbox-group-2" v-model="form.testIds">
              <div>
                <b-form-checkbox
                  class="my-3"
                  style="display:flex; flex-direction: column"
                  v-for="test in chunk"
                  :key="test.value"
                  :value="test.value"
                  :disabled="form.laboratoryPanel.label != 'Custom'"
                >
                  {{ test.text }}
                </b-form-checkbox>
              </div>
            </b-form-checkbox-group>
          </b-form-group></b-col
        >
      </b-row>
    </b-form-group>
  </b-modal>
</template>
<script>
import { mapGetters } from "vuex";
import { formatDateTimeMysql, formatDateTimeSql } from "../../helpers/date";
import { validateRequired, validateDateTime } from "../../helpers/validation";
import EocPicker from "../controls/EocPicker";
import DateTimePicker from "../controls/DateTimePicker.vue";
import eocServer from "../../server/eoc";
import { chunks } from "../../helpers/general";
import labsServer from "../../server/labs";
export default {
  components: { EocPicker, DateTimePicker },
  data() {
    return {
      activeClients: [],
      laboratories: [],
      panels: null,
      tests: null,
      eoc: null,
      form: {
        laboratory: null,
        episodeOfCare: null,
        collectionDateTime: null,
        laboratoryPanel: null,
        testIds: []
      },
      formErrors: {
        episodeOfCare: [],
        laboratory: [],
        collectionDateTime: [],
        testIds: []
      }
    };
  },
  async mounted() {
    this.fetchLaboratories();
    const currentAdjustedDateTime = this.getCurrentAdjustedDateTime();
    this.getActiveClients();
    this.form.collectionDateTime = formatDateTimeMysql(currentAdjustedDateTime);
  },
  methods: {
    async fetchLaboratories() {
      const laboratoriesList = await labsServer.findAllLaboratories();
      this.laboratories = laboratoriesList.map(lab => ({
        value: lab.id,
        label: lab.name
      }));
    },
    fetchPanelsAndTests() {
      this.fetchPanels();
      this.fetchTests();
    },
    async fetchPanels() {
      const panelsList = await labsServer.findPanelsByLaboratory(
        this.form.laboratory.value
      );
      var custom = [
        {
          value: null,
          label: "Custom"
        }
      ];
      this.panels = custom.concat(
        panelsList.map(panel => ({
          value: panel.id,
          label: panel.name,
          tests: panel.tests
        }))
      );
      this.form.laboratoryPanel = this.panels[0];
    },
    async fetchTests() {
      const testsList = await labsServer.findTestsByLaboratory(
        this.form.laboratory.value
      );
      this.tests = testsList.map(test => ({
        value: test.id,
        text: test.name
      }));
    },
    validateLaboratoryOrderSubmit() {
      let withoutErrors = true;

      this.formErrors.episodeOfCare = [];
      if (!validateRequired(this.form.episodeOfCare)) {
        this.formErrors.episodeOfCare.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.collectionDateTime = [];
      if (!validateRequired(this.form.collectionDateTime)) {
        this.formErrors.collectionDateTime.push("This field is required");
        withoutErrors = false;
      }
      if (
        this.form.collectionDateTime &&
        !validateDateTime(this.form.collectionDateTime)
      ) {
        this.formErrors.collectionDateTime.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }
      this.formErrors.testIds = [];
      if (this.form.testIds.length == 0) {
        this.formErrors.testIds.push("Must select at least one test");
        withoutErrors = false;
      }

      this.formErrors.laboratory = [];
      if (!validateRequired(this.form.laboratory)) {
        this.formErrors.laboratory.push("This field is required");
        withoutErrors = false;
      }
      return withoutErrors;
    },
    onHidden() {},

    close() {
      this.$emit("onModify");
    },

    async submitLaboratoryOrder(modalEvent) {
      modalEvent.preventDefault();
      const validation = this.validateLaboratoryOrderSubmit();
      if (validation) {
        const formData = {
          laboratoryId: this.form.laboratory.value,
          episodeOfCareId: this.form.episodeOfCare.value,
          collectionDateTime: formatDateTimeSql(this.form.collectionDateTime),
          laboratoryPanelId: this.form.laboratoryPanel.value,
          testIds: this.form.testIds
        };
        await labsServer.create(formData);
        this.$emit("onModify");
      }
    },
    async fetchEocsOptionsPicker(search, loading) {
      if (this.lastTimeoutEocsOptionsPicker) {
        clearTimeout(this.lastTimeoutEocsOptionsPicker);
        this.lastTimeoutEocsOptionsPicker = null;
      }
      if (search && search.trim().length >= 3) {
        this.activeClients = [];
      }
      this.lastTimeoutEocsOptionsPicker = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          if (search.length >= 3) {
            loading(true);
          }

          this.searchingEocs = true;
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              locations: this.locationId ? [this.locationId] : [],
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );

          this.activeClients = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc
          }));
          this.searchingEocs = false;
          if (search.length >= 3) {
            loading(false);
          }
        }
      }, 1000);
    },
    async getActiveClients() {
      const optionsResult = await eocServer.queryList({
        searchPhrase: "",
        eocStatus: ["Active", "Pending"],
        locationId: [],
        page: 1,
        pageSize: 50
      });
      this.activeClients = optionsResult.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));
    },
    chunks(values, chunkCount) {
      return chunks(values, chunkCount);
    }
  },
  computed: {
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  watch: {
    "form.laboratoryPanel": function(newValue) {
      if (newValue.label == "Custom") {
        this.form.testIds = [];
      } else {
        this.form.testIds = newValue.tests.map(t => t.id);
      }
    }
  }
};
</script>
